import { useEffect, useState } from "react";
import WebApp from "@twa-dev/sdk";

export type SafeAreaInsets = {
  top: number;
  bottom: number;
  left: number;
  right: number;
};

type InsetSource = { top?: number; bottom?: number; left?: number; right?: number } | undefined;

function readInsets(): SafeAreaInsets {
  const w = WebApp as unknown as {
    safeAreaInset?: InsetSource;
    contentSafeAreaInset?: InsetSource;
  };
  const sa = w.safeAreaInset ?? {};
  const csa = w.contentSafeAreaInset ?? {};
  return {
    top: Math.max(Number(sa.top) || 0, Number(csa.top) || 0),
    bottom: Math.max(Number(sa.bottom) || 0, Number(csa.bottom) || 0),
    left: Math.max(Number(sa.left) || 0, Number(csa.left) || 0),
    right: Math.max(Number(sa.right) || 0, Number(csa.right) || 0),
  };
}

// Возвращает реальные safe-area insets через JS API Telegram WebApp.
// В iOS fullscreen CSS-переменные --tg-safe-area-inset-* и env(safe-area-inset-*)
// внутри iframe Telegram не всегда покрывают Dynamic Island — JS API надёжнее.
// Подписываемся на safeAreaChanged / contentSafeAreaChanged / viewportChanged
// чтобы пересчитывать при смене fullscreen/повороте.
export function useSafeAreaInsets(): SafeAreaInsets {
  const [insets, setInsets] = useState<SafeAreaInsets>(() => {
    try {
      return readInsets();
    } catch {
      return { top: 0, bottom: 0, left: 0, right: 0 };
    }
  });

  useEffect(() => {
    const update = () => {
      try {
        setInsets(readInsets());
      } catch {
        // ignore
      }
    };

    const w = WebApp as unknown as {
      onEvent: (name: string, cb: () => void) => void;
      offEvent: (name: string, cb: () => void) => void;
    };

    try {
      w.onEvent("safeAreaChanged", update);
      w.onEvent("contentSafeAreaChanged", update);
      WebApp.onEvent("viewportChanged", update);
    } catch {
      // не в Telegram — игнор
    }

    // Дополнительная страховка: ловим первый кадр после `ready()`,
    // когда Telegram уже успел проставить insets.
    const t1 = setTimeout(update, 0);
    const t2 = setTimeout(update, 200);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      try {
        w.offEvent("safeAreaChanged", update);
        w.offEvent("contentSafeAreaChanged", update);
        WebApp.offEvent("viewportChanged", update);
      } catch {
        // ignore
      }
    };
  }, []);

  return insets;
}

// Старое имя оставлено для обратной совместимости — теперь просто
// возвращает «версию» инсетов как номер тика (чтобы компонент перерисовался).
export function useSafeAreaTick(): number {
  const i = useSafeAreaInsets();
  return i.top + i.bottom + i.left + i.right;
}

// В fullscreen-режиме Telegram рисует поверх веб-вью свои капсулы
// («Закрыть» слева и «свернуть/меню» справа). contentSafeAreaInset
// не всегда честно отдаёт их ширину на iOS — поэтому держим флаг
// отдельно и накладываем жёсткий fallback по горизонтали.
export function useIsFullscreen(): boolean {
  const [full, setFull] = useState<boolean>(() => {
    try {
      return Boolean((WebApp as unknown as { isFullscreen?: boolean }).isFullscreen);
    } catch {
      return false;
    }
  });

  useEffect(() => {
    const read = () => {
      try {
        setFull(Boolean((WebApp as unknown as { isFullscreen?: boolean }).isFullscreen));
      } catch {
        // ignore
      }
    };

    const w = WebApp as unknown as {
      onEvent: (name: string, cb: () => void) => void;
      offEvent: (name: string, cb: () => void) => void;
    };

    try {
      w.onEvent("fullscreenChanged", read);
      w.onEvent("fullscreenFailed", read);
    } catch {
      // не в Telegram — игнор
    }

    const t1 = setTimeout(read, 0);
    const t2 = setTimeout(read, 200);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      try {
        w.offEvent("fullscreenChanged", read);
        w.offEvent("fullscreenFailed", read);
      } catch {
        // ignore
      }
    };
  }, []);

  return full;
}
